/* eslint-disable react/require-default-props */
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
/**
 * Determines the file type category based on MIME type
 */
function getFileType(file) {
    if (file.type.startsWith('image/'))
        return 'image';
    if (file.type.startsWith('audio/'))
        return 'audio';
    return 'document';
}
/**
 * Generates a unique ID for attachments
 */
function generateId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
const AIInputContext = createContext(null);
/**
 * AIInput Context Provider
 * Manages all state for the AIInput organism
 * @version 1.0.0
 * @since 2026-02-17
 */
export function AIInputProvider({ allowedFormats, maxFiles = 10, defaultValue, children }) {
    const [text, setText] = useState(defaultValue || '');
    const [attachments, setAttachments] = useState([]);
    const [isAttachMenuOpen, setAttachMenuOpen] = useState(false);
    // Sync text when defaultValue changes (e.g., retry functionality)
    useEffect(() => {
        if (defaultValue !== undefined) {
            setText(defaultValue);
        }
    }, [defaultValue]);
    const addAttachments = useCallback((files) => {
        const filtered = allowedFormats
            ? files.filter((file) => {
                const extension = file.name.split('.').pop();
                const ext = extension ? `.${extension.toLowerCase()}` : '';
                return allowedFormats.some((fmt) => fmt.toLowerCase() === ext);
            })
            : files;
        setAttachments((prev) => {
            const remaining = maxFiles - prev.length;
            if (remaining <= 0)
                return prev;
            const toAdd = filtered.slice(0, remaining).map((file) => ({
                id: generateId(),
                file,
                name: file.name,
                type: getFileType(file)
            }));
            return [...prev, ...toAdd];
        });
    }, [allowedFormats, maxFiles]);
    const removeAttachment = useCallback((id) => {
        setAttachments((prev) => prev.filter((a) => a.id !== id));
    }, []);
    const clearAll = useCallback(() => {
        setText('');
        setAttachments([]);
    }, []);
    const value = useMemo(() => ({
        text,
        setText,
        attachments,
        addAttachments,
        removeAttachment,
        clearAll,
        isAttachMenuOpen,
        setAttachMenuOpen
    }), [text, attachments, addAttachments, removeAttachment, clearAll, isAttachMenuOpen]);
    return React.createElement(AIInputContext.Provider, { value: value }, children);
}
/**
 * Hook to consume AIInput context
 * Must be used within an AIInputProvider
 */
export function useAIInput() {
    const context = useContext(AIInputContext);
    if (!context) {
        throw new Error('useAIInput must be used within an AIInputProvider');
    }
    return context;
}
